import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { GoogleLogin } from '@react-oauth/google';

const LoginPage: React.FC = () => {
  const { login, error, clearError, isLoading } = useAuth();

  const handleLoginSuccess = (credentialResponse: any) => {
    if (credentialResponse.credential) {
      login(credentialResponse.credential);
    }
  };

  const handleLoginError = () => {
    // Google OAuth error handling is done automatically
    console.log('Google OAuth login failed');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg mx-auto mb-6">
            <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent mb-2">
            Maksimer Dashboard
          </h1>
          <p className="text-slate-600 font-medium">
            Logg inn for å få tilgang til KPI og økonomiske indikatorer
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-2xl border border-slate-200/60 p-8">
          {/* Domain Restriction Notice */}
          <div className="mb-6 p-4 bg-blue-50/80 rounded-xl border border-blue-200/60">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-blue-800 mb-1">
                  Tilgang begrenset
                </h3>
                <p className="text-sm text-blue-700">
                  Kun ansatte med <strong>@maksimer.com</strong> eller <strong>@maksimer.no</strong> e-postadresser kan logge inn.
                </p>
              </div>
            </div>
          </div>

          {/* Google Login Button */}
          <div className="space-y-4">
            <GoogleLogin
              onSuccess={handleLoginSuccess}
              onError={handleLoginError}
              useOneTap={false}
              theme="outline"
              size="large"
              text="signin_with"
              shape="rectangular"
              locale="no"
              width="100%"
            />
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="mt-6 flex items-center justify-center space-x-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-600"></div>
              <span className="text-sm text-slate-600 font-medium">Logger inn...</span>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="mt-6 p-4 bg-red-50/80 rounded-xl border border-red-200/60">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-red-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-red-800 mb-1">
                    Innlogging mislyktes
                  </h3>
                  <p className="text-sm text-red-700 mb-3">
                    {error}
                  </p>
                  <button
                    onClick={clearError}
                    className="text-sm text-red-600 hover:text-red-800 font-medium underline"
                  >
                    Prøv igjen
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-slate-200/60">
            <p className="text-xs text-slate-500 text-center">
              Sikker innlogging via Google OAuth 2.0
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
